import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useDebouncedSearch } from '@/client/hooks/useDebouncedSearch';

interface UseListFiltersOptions<T> {
  initialFilters: T;
  initialPage?: number;
  initialLimit?: number;
  debounceMs?: number;
  namespace?: string;
}

export function useListFilters<T extends Record<string, any>>({
  initialFilters,
  initialPage = 1,
  initialLimit = 10,
  debounceMs = 500,
  namespace,
}: UseListFiltersOptions<T>) {
  const [searchParams, setSearchParams] = useSearchParams();

  const configRef = useRef<{
    keys: (keyof T & string)[];
    defaults: T;
    initialPage: number;
    initialLimit: number;
  } | null>(null);
  if (!configRef.current) {
    configRef.current = {
      keys: Object.keys(initialFilters) as (keyof T & string)[],
      defaults: initialFilters,
      initialPage,
      initialLimit,
    };
  }
  const { keys, defaults } = configRef.current;

  const prefix = namespace ? `${namespace}_` : '';
  const qKey = `${prefix}q`;
  const pageKey = `${prefix}page`;
  const limitKey = `${prefix}limit`;
  const filterKey = useCallback((k: string) => `${prefix}${k}`, [prefix]);

  const defaultStr = useCallback(
    (k: string) => {
      const d = (defaults as any)[k];
      return d === undefined || d === null ? '' : String(d);
    },
    [defaults],
  );

  // Search stays local for a responsive input; the debounced value (normalized,
  // instant on clear) is what gets written to the URL and consumed by queries.
  const {
    value: searchTerm,
    setValue: setSearchTerm,
    debounced: debouncedSearchTerm,
    isDebouncing: isSearchPending,
  } = useDebouncedSearch(searchParams.get(qKey) ?? '', { delay: debounceMs });

  const patchParams = useCallback(
    (mutate: (params: URLSearchParams) => void) => {
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          mutate(next);
          return next;
        },
        { replace: true },
      );
    },
    [setSearchParams],
  );

  // Tracks the search value already reflected in the URL, so hydrating on mount
  // (or an external reset) doesn't spuriously fire a write / page reset.
  const syncedSearchRef = useRef<string>(searchParams.get(qKey) ?? '');
  useEffect(() => {
    if (debouncedSearchTerm === syncedSearchRef.current) return;
    syncedSearchRef.current = debouncedSearchTerm;
    patchParams((p) => {
      if (debouncedSearchTerm) p.set(qKey, debouncedSearchTerm);
      else p.delete(qKey);
      p.set(pageKey, '1');
    });
  }, [debouncedSearchTerm, patchParams, qKey, pageKey]);

  const filters = useMemo(() => {
    const out = {} as T;
    keys.forEach((k) => {
      const v = searchParams.get(filterKey(k));
      (out as any)[k] = v !== null ? v : (defaults as any)[k];
    });
    return out;
  }, [searchParams, keys, defaults, filterKey]);

  const page = Math.max(1, Number(searchParams.get(pageKey)) || configRef.current.initialPage);
  const limit = Math.max(1, Number(searchParams.get(limitKey)) || configRef.current.initialLimit);

  const handleSearch = useCallback((query: string) => {
    // The debounced effect mirrors this to the URL and resets to page 1.
    setSearchTerm(query);
  }, []);

  const writeFilter = useCallback(
    (params: URLSearchParams, k: string, value: unknown) => {
      const str = value === undefined || value === null ? '' : String(value);
      // Drop a param only when it equals its default — an absent param and its
      // default resolve to the same thing, so this keeps URLs clean without
      // losing information. When the default is non-empty (e.g. status
      // 'under_review'), an explicit empty value is a real override and is kept
      // (`?status=`), so it survives a reload instead of snapping back.
      if (str === defaultStr(k)) params.delete(filterKey(k));
      else params.set(filterKey(k), str);
    },
    [defaultStr, filterKey],
  );

  const handleFilterChange = useCallback(
    (key: keyof T, value: any) => {
      patchParams((p) => {
        writeFilter(p, key as string, value);
        p.set(pageKey, '1');
      });
    },
    [patchParams, writeFilter, pageKey],
  );

  // `resetPage` goes in the same URL write: a separate handlePageChange in the
  // same tick reads the pre-update params and would drop these filters.
  const setFilters = useCallback(
    (updater: Partial<T> | ((prev: T) => T), options?: { resetPage?: boolean }) => {
      patchParams((p) => {
        const prevFilters = {} as T;
        keys.forEach((k) => {
          const v = p.get(filterKey(k));
          (prevFilters as any)[k] = v !== null ? v : (defaults as any)[k];
        });
        const nextFilters =
          typeof updater === 'function'
            ? (updater as (prev: T) => T)(prevFilters)
            : { ...prevFilters, ...updater };
        keys.forEach((k) => writeFilter(p, k, (nextFilters as any)[k]));
        if (options?.resetPage) p.set(pageKey, '1');
      });
    },
    [patchParams, keys, defaults, filterKey, writeFilter, pageKey],
  );

  const handlePageChange = useCallback(
    (newPage: number) => {
      patchParams((p) => {
        if (newPage <= configRef.current!.initialPage) p.delete(pageKey);
        else p.set(pageKey, String(newPage));
      });
    },
    [patchParams, pageKey],
  );

  const handleLimitChange = useCallback(
    (newLimit: number) => {
      patchParams((p) => {
        if (newLimit === configRef.current!.initialLimit) p.delete(limitKey);
        else p.set(limitKey, String(newLimit));
        p.set(pageKey, '1');
      });
    },
    [patchParams, limitKey, pageKey],
  );

  const resetFilters = useCallback(() => {
    setSearchTerm('');
    syncedSearchRef.current = '';
    patchParams((p) => {
      p.delete(qKey);
      p.delete(pageKey);
      keys.forEach((k) => p.delete(filterKey(k)));
    });
  }, [patchParams, qKey, pageKey, keys, filterKey]);

  return {
    searchTerm,
    debouncedSearchTerm,
    isSearchPending,
    filters,
    page,
    limit,
    handleSearch,
    handleFilterChange,
    handlePageChange,
    handleLimitChange,
    setFilters,
    resetFilters,
  };
}
